import { Directive, ElementRef, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@UntilDestroy()
@Directive({
    selector: '[khIntersection]',
    exportAs: 'intersection',
    standalone: false
})
export class IntersectionDirective implements OnInit {
  @Input() public root: HTMLElement | null = null;
  @Input() public rootMargin = '0px';
  @Input() public threshold = 0.8;
  @Input() public debounceTime = 0;
  @Input() public isContinuous = false;
  @Output() public isIntersecting = new EventEmitter<boolean>();
  public intersecting = false;

  constructor(private readonly element: ElementRef) {
  }

  public ngOnInit(): void {
    this.subscribeAndObserve();
  }

  private subscribeAndObserve(): void {
    const options: IntersectionObserverInit = {
      root: this.root,
      rootMargin: this.rootMargin,
      threshold: this.threshold,
    }

    new Observable<boolean>(subscriber => {
      const intersectionObserver = new IntersectionObserver(entries => {
        const { isIntersecting } = entries[0];
        subscriber.next(isIntersecting);

        if (isIntersecting && !this.isContinuous) {
          intersectionObserver.disconnect();
        }
      }, options);

      intersectionObserver.observe(this.element.nativeElement);

      return {
        unsubscribe() {
          intersectionObserver.disconnect();
        }
      };
    })
      .pipe(
        debounceTime(this.debounceTime),
        untilDestroyed(this)
      )
      .subscribe(status => {
        this.isIntersecting.emit(status);
        this.intersecting = status;
      });
  }
}
